import { useCallback, useContext, useState } from "react"
import { refScope } from "../../../../types/gqlTypes/graphql"
import { ProgramsContext } from "../../../mp-shared-components/ready-shared-components/components/Navs"
import {
  getPdfGeneratorStylesheetUrls,
  usePdfGenerator
} from "../../../mp-shared-components/generic-shared-components/components/PDFGenerator"
import { ManagementTheme } from "../../../mp-core/themes"
import {
  getGeneratePdfOptions,
  getStudentPerformanceReportData
} from "./helpers"
import { useSelector } from "react-redux"
import { RootState } from "../../../mp-core/redux/store"
import { User } from "../../../mp-database"
import { getOrgRefScope } from "../tableDataProviders/helpers"
import { enqueueSnackbar } from "notistack"

export const useGenerateStudentPerformanceReport = () => {
  const { programsAndCourses } = useContext(ProgramsContext)

  const user = useSelector((state: RootState) => state.users.testUser as User)

  const [loading, setLoading] = useState<boolean>(false)

  const { generate } = usePdfGenerator({
    theme: ManagementTheme,
    stylesheetUrls: getPdfGeneratorStylesheetUrls([])
  })

  const generateReport = useCallback(
    async (contentId: string, studentId: string, testName: string) => {
      setLoading(true)

      try {
        if (!contentId || !studentId) {
          throw Error("ContentId is not provided")
        }

        const orgRefScope = getOrgRefScope({
          user,
          filters: {},
          isGraphQLProvider: true
        })

        const studentRefScope: refScope = {
          scope: "STUDENT",
          student_id: studentId,
          ...(!!orgRefScope?.classId && { class_id: orgRefScope?.classId }),
          ...(!!orgRefScope?.districtId && {
            district_id: orgRefScope?.districtId
          }),
          ...(!!orgRefScope?.schoolId && { school_id: orgRefScope?.schoolId }),
          ...(!!orgRefScope?.teacherId && {
            teacher_id: orgRefScope?.teacherId
          })
        }

        const reportData = await getStudentPerformanceReportData({
          contentId,
          studentRefScope,
          programsAndCourses
        })

        if (!reportData) {
          throw Error("Report Data is empty")
        }

        const {
          testName,
          studentName,
          programDisplayName,
          subtestsData,
          isOverallAvailable,
          overallScore,
          overallMinScore,
          overallMaxScore
        } = reportData

        const pdfOptions = await getGeneratePdfOptions({
          testName,
          studentName,
          programDisplayName,
          subtestsData,
          isOverallAvailable,
          overallScore,
          overallMinScore,
          overallMaxScore
        })

        const result = await generate(pdfOptions)

        if (!result) {
          throw Error("PDF generaion failed.")
        }

        enqueueSnackbar(`${result.fileName} successfully downloaded.`, {
          variant: "success"
        })

        return result
      } catch (error) {
        enqueueSnackbar(
          `${
            !!testName ? `${testName} ` : ""
          }Performance Report.pdf download is currently unavailable. Try again later or contact support if the issue persists.`,
          { variant: "error" }
        )

        return null
      } finally {
        setLoading(false)
      }
    },
    [user, programsAndCourses, generate]
  )

  return {
    loading,
    generateReport
  }
}
